import type { HPOTerm } from '@/components/common/HPOSearch'

export type ParentPhenotypeLabels = {
  key: string[]
  additional: string[]
  pertinentNegative: string[]
}

/** Group proband-modal HPO terms for the biological parent summary card. */
export function phenotypeLabelsFromMemberTerms(terms: HPOTerm[]): ParentPhenotypeLabels {
  const key: string[] = []
  const additional: string[] = []
  const pertinentNegative: string[] = []
  for (const t of terms) {
    const label = t.name?.trim()
    if (!label) continue
    if (t.type === 'key') key.push(label)
    else if (t.type === 'additional') additional.push(label)
    else if (t.type === 'excluded') pertinentNegative.push(label)
  }
  return { key, additional, pertinentNegative }
}

/** Group canonical `hpoTerms` (CategorizedHPOOption shape) for read-only order views. */
export function phenotypeLabelsFromCanonicalHpo(terms: unknown): ParentPhenotypeLabels {
  const key: string[] = []
  const additional: string[] = []
  const pertinentNegative: string[] = []
  if (!Array.isArray(terms)) return { key, additional, pertinentNegative }
  for (const raw of terms) {
    const t = raw as { label?: string; category?: string }
    const label = t.label?.trim() ?? ''
    if (!label) continue
    if (t.category === 'Key') key.push(label)
    else if (t.category === 'Additional') additional.push(label)
    else if (t.category === 'Excluded') pertinentNegative.push(label)
  }
  return { key, additional, pertinentNegative }
}

/**
 * Same as `phenotypeLabelsFromCanonicalHpo`, but substitutes the term's display
 * label using `overrides[id]` when present. Used by views that asynchronously
 * resolve human-readable labels for legacy/HP-coded canonical data.
 */
export function phenotypeLabelsFromCanonicalHpoWithOverrides(
  terms: unknown,
  overrides: Record<string, string>
): ParentPhenotypeLabels {
  const key: string[] = []
  const additional: string[] = []
  const pertinentNegative: string[] = []
  if (!Array.isArray(terms)) return { key, additional, pertinentNegative }
  for (const raw of terms) {
    const t = raw as { id?: string; label?: string; category?: string }
    const id = String(t.id ?? '').trim()
    const rawLabel = (t.label ?? '').trim()
    const override = id ? overrides[id]?.trim() : undefined
    const display = override || rawLabel
    if (!display) continue
    if (t.category === 'Key') key.push(display)
    else if (t.category === 'Additional') additional.push(display)
    else if (t.category === 'Excluded') pertinentNegative.push(display)
  }
  return { key, additional, pertinentNegative }
}
